"use strict";
/**
 * Novig order creation — the intent the server prices (ai/novig/phase-5-orders.md §5).
 *
 * SYNCHRONOUS: on the click→exchange path. No book is read here: the server
 * prices from its own live book, so the client sends the outcome, the budget,
 * and either a slippage or a manual price, chosen by the same rules as the
 * other venues (orders/creation.ts). A sell names the outcome sold and how
 * much; the server sizes it from its positions (ai/NOVIG_VENUE_PLAN.md phase 6b).
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.createNovigOrderRequest = createNovigOrderRequest;
exports.createNovigSellRequest = createNovigSellRequest;
exports.createNovigBuyRequest = createNovigBuyRequest;
const permissions_1 = require("../permissions");
const state_1 = require("../state");
const sport_mode_1 = require("../ui/sport-mode");
const slippage_utils_1 = require("./slippage-utils");
function createNovigOrderRequest(ctx) {
    return ctx.action === "sell" ? createNovigSellRequest(ctx) : createNovigBuyRequest(ctx);
}
/**
 * The clicked slot's outcome. A fan-out leg carries its market's ids; anything
 * else must be the market the operator is looking at — an order for any other
 * market could trade an outcome nobody saw labelled.
 */
function clickedOutcome(ctx) {
    const slot = ctx.side === "yes" ? 0 : 1;
    if (ctx.options.novigOutcomeIds)
        return ctx.options.novigOutcomeIds[slot];
    const market = (0, state_1.getState)().selectedMarket;
    if (!market || market.id !== ctx.marketId || !market.novigOutcomeIds) {
        throw new Error("Novig market is not loaded");
    }
    return market.novigOutcomeIds[slot];
}
/** A sell takes any price, like the other venues', so it carries no price and no budget. */
function createNovigSellRequest(ctx) {
    const outcomeId = clickedOutcome(ctx);
    const { sellMode, fixedSellQuantity } = (0, state_1.getState)().tradingSettings;
    return sellMode === "partial"
        ? { action: "sell", marketId: ctx.marketId, outcomeId, sellMode, sellContracts: fixedSellQuantity, clickId: ctx.options.clickId }
        : { action: "sell", marketId: ctx.marketId, outcomeId, sellMode: "full", clickId: ctx.options.clickId };
}
function createNovigBuyRequest(ctx) {
    const { side, options } = ctx;
    const outcomeId = clickedOutcome(ctx);
    const { tradingSettings, permissions } = (0, state_1.getState)();
    const dollarAmount = options.dollarAmount ?? tradingSettings.dollarAmount;
    if (!(dollarAmount > 0))
        throw new Error("Dollar amount must be greater than 0");
    // Tagged so the server can gate it (allowParallelMarkets / allowAdditionalMarkets).
    const leg = options.parallelMarket
        ? { parallelMarket: true }
        : options.additionalMarket
            ? { additionalMarket: true }
            : null;
    // Owner-gated (server-enforced too): the server adds the other side's holding.
    // Never on a leg: it adds exposure on its own market.
    const closeOpposite = !leg && tradingSettings.closeOppositeOnBuy === true && (0, permissions_1.permsAllowCloseOpposite)(permissions);
    const base = {
        marketId: ctx.marketId,
        outcomeId,
        clickId: options.clickId,
        dollarAmount,
        ...(closeOpposite ? { closeOpposite } : {}),
        ...leg,
    };
    // Per-button price (sport layouts with manual prices).
    if (options.manualPrice !== undefined)
        return { ...base, manualPriceCents: options.manualPrice };
    // Global manual price mode, when no sport layout is showing.
    if (tradingSettings.manualPriceMode && !(0, sport_mode_1.isSportLayoutActive)(tradingSettings)) {
        const selected = side === "yes" ? tradingSettings.manualYesPriceSelected : tradingSettings.manualNoPriceSelected;
        if (selected !== true) {
            throw new Error(`Select a manual ${side.toUpperCase()} price before placing an order`);
        }
        return {
            ...base,
            manualPriceCents: side === "yes" ? tradingSettings.manualYesPrice : tradingSettings.manualNoPrice,
        };
    }
    return {
        ...base,
        slippageCents: options.overrideSlippage ?? (0, slippage_utils_1.getEffectiveSlippage)(tradingSettings, side),
    };
}
